import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';

const LoggedOutScreen: React.FC = () => {
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSuccess = async (credentialResponse: any) => {
    if (!credentialResponse.credential) {
      setError('Authentication failed. Please try again.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await login(credentialResponse.credential);
    } catch (error: any) {
      setError(error.message || 'Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleError = () => {
    setError('Authentication failed. Please try again.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-coolGray-900 to-coolGray-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img 
              src="/salesforcelogo-square-120.png" 
              alt="Salesforce Demo Hub" 
              className="h-16 w-auto"
            />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Welcome Back
          </h1>
          <p className="text-coolGray-400 text-lg">
            Sign in to access your Salesforce Demo Hub
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-coolGray-800 rounded-lg shadow-xl p-8 border border-coolGray-700">
          {/* Google Sign In */}
          <div className="mb-6">
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleSuccess}
                onError={handleError}
                useOneTap={false}
                theme="filled_blue"
                size="large"
                text="signin_with"
                shape="rectangular"
                width="300"
              />
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-900/20 border border-red-700/30 rounded-md p-4 mb-4">
              <div className="flex items-start space-x-3">
                <svg className="flex-shrink-0 mt-0.5 w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-red-300 mb-2">
                    Authentication Error
                  </h3>
                  <p className="text-sm text-red-200 leading-relaxed">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
            </div>
          )}

          {/* Domain Restriction Notice */}
          <div className="bg-blue-900/20 border border-blue-700/30 rounded-md p-4">
            <div className="flex items-start space-x-3">
              <svg className="flex-shrink-0 mt-0.5 w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-blue-300 mb-2">
                  Access Restricted
                </h3>
                <p className="text-sm text-blue-200 leading-relaxed">
                  This application is restricted to users with @salesforce.com email addresses only. Please use your Salesforce credentials to sign in.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-coolGray-500 text-sm">
              Secure authentication powered by Google
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LoggedOutScreen;
